import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../../mocks/blog';
import { useTheme } from '../../hooks/useTheme';
import BlogHero from './components/BlogHero';
import ArticleCard from './components/ArticleCard';
import BlogCTA from './components/BlogCTA';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://innoflextechnology.com';
const blogUrl = `${siteUrl}/blog`;

const LOGO_URL = 'https://public.readdy.ai/ai/img_res/30ce4755-e8b9-4ea3-9fec-24f4c0149c4e.png';

const CATEGORIES = [
  { label: 'Facturación e-CF', color: '#00cfff' },
  { label: 'DGII & Normativa', color: '#f97316' },
  { label: 'Software ERP', color: '#10b981' },
];

export default function BlogPage() {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Todos');

  // Inject Blog CollectionPage Schema.org JSON-LD
  useEffect(() => {
    const schemaId = 'blog-page-jsonld';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();

    const blogSchema = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      '@id': `${blogUrl}#blog`,
      name: 'Blog INNOFLEX — Recursos de Facturación e-CF y DGII',
      description:
        'Artículos, guías y normativa actualizada sobre facturación electrónica e-CF y DGII en República Dominicana. Aprende a implementar e-CF en tu negocio con InnoFlex.',
      url: blogUrl,
      inLanguage: 'es',
      publisher: {
        '@type': 'Organization',
        name: 'INNOFLEX',
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/221e836cb47596545a3a537d495fa5e5/2baae45286386b606f279993b0bc93de.jpeg',
          width: 200,
          height: 200,
        },
      },
      blogPost: blogPosts.map((p) => ({
        '@type': 'BlogPosting',
        headline: p.title,
        description: p.excerpt,
        url: `${siteUrl}/blog/${p.slug}`,
        datePublished: p.date,
        author: {
          '@type': 'Person',
          name: p.author,
        },
        image: p.coverImage,
        keywords: p.tags.join(', '),
      })),
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'INNOFLEX',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: blogUrl,
        },
      ],
    };

    const collectionSchema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${blogUrl}#collectionpage`,
      name: 'Blog e-CF y DGII | InnoFlex',
      description:
        'Artículos sobre facturación electrónica e-CF, normativa DGII y software ERP para negocios en República Dominicana.',
      url: blogUrl,
      inLanguage: 'es',
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = schemaId;
    script.textContent = JSON.stringify([blogSchema, breadcrumbSchema, collectionSchema]);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const filteredPosts = useMemo(() => {
    let posts = [...blogPosts];
    if (activeCategory !== 'Todos') {
      posts = posts.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      posts = posts.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    return posts;
  }, [searchQuery, activeCategory]);

  const featured = filteredPosts.find((p) => p.featured);
  const rest = filteredPosts.filter((p) => !p.featured || activeCategory !== 'Todos' || searchQuery);
  const gridPosts = featured && activeCategory === 'Todos' && !searchQuery
    ? filteredPosts.filter((p) => !p.featured)
    : filteredPosts;

  const bgStyle = isLight
    ? { backgroundColor: '#f8fafc' }
    : { backgroundColor: '#080f1f' };

  return (
    <div style={bgStyle} className="min-h-screen">
      <title>Blog e-CF y DGII | InnoFlex — Recursos de Facturación Electrónica en República Dominicana</title>
      <meta
        name="description"
        content="Artículos, guías y normativa actualizada sobre facturación electrónica e-CF y DGII en República Dominicana. Aprende a implementar e-CF en tu negocio con InnoFlex."
      />
      <meta name="keywords" content="e-CF República Dominicana, DGII facturación electrónica, comprobante fiscal electrónico RD, software facturación DGII, blog ERP RD" />
      <link rel="canonical" href={blogUrl} />
      <meta property="og:title" content="Blog e-CF y DGII | InnoFlex" />
      <meta property="og:description" content="Guías, normativa y recursos sobre facturación electrónica e-CF en República Dominicana." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={blogUrl} />
      <meta property="og:locale" content="es_DO" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Blog e-CF y DGII | InnoFlex" />
      <meta name="twitter:description" content="Guías, normativa y recursos sobre facturación electrónica e-CF en República Dominicana." />

      <Navbar />

      <BlogHero
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        categories={CATEGORIES}
        totalArticles={blogPosts.length}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Featured article */}
        {featured && activeCategory === 'Todos' && !searchQuery && (
          <div className="mb-10">
            <ArticleCard post={featured} featured />
          </div>
        )}

        {/* Results header */}
        {(searchQuery || activeCategory !== 'Todos') && (
          <div className="mb-6 flex items-center justify-between">
            <p className={`font-body text-sm ${isLight ? 'text-navy-600' : 'text-white/50'}`}>
              {filteredPosts.length === 0
                ? 'No se encontraron artículos'
                : `${filteredPosts.length} artículo${filteredPosts.length !== 1 ? 's' : ''} encontrado${filteredPosts.length !== 1 ? 's' : ''}`}
            </p>
            {(searchQuery || activeCategory !== 'Todos') && (
              <button
                onClick={() => { setSearchQuery(''); setActiveCategory('Todos'); }}
                className="flex items-center gap-1.5 text-electric text-xs font-semibold font-body cursor-pointer hover:underline"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-close-line text-xs" />
                </div>
                Limpiar filtros
              </button>
            )}
          </div>
        )}

        {/* Section label for grid */}
        {!searchQuery && activeCategory === 'Todos' && (
          <div className="flex items-center gap-3 mb-6">
            <h2 className={`font-heading text-lg font-bold ${isLight ? 'text-navy-900' : 'text-white'}`}>
              Todos los artículos
            </h2>
            <div className="flex-1 h-px" style={{ backgroundColor: isLight ? 'rgba(10,22,40,0.06)' : 'rgba(255,255,255,0.06)' }} />
          </div>
        )}

        {/* Articles grid */}
        {filteredPosts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {gridPosts.map((post) => (
              <ArticleCard key={post.slug} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className={`w-16 h-16 flex items-center justify-center rounded-2xl mx-auto mb-4 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`}>
              <i className={`ri-search-line text-3xl ${isLight ? 'text-slate-400' : 'text-white/30'}`} />
            </div>
            <h3 className={`font-heading text-xl font-bold mb-2 ${isLight ? 'text-navy-800' : 'text-white'}`}>
              No hay resultados
            </h3>
            <p className={`font-body text-sm mb-6 ${isLight ? 'text-navy-500' : 'text-white/40'}`}>
              Intenta con otros términos o explora todas las categorías.
            </p>
            <button
              onClick={() => { setSearchQuery(''); setActiveCategory('Todos'); }}
              className="inline-flex items-center gap-2 bg-electric/10 hover:bg-electric/20 text-electric text-sm font-semibold px-5 py-2.5 rounded-full transition-colors cursor-pointer font-body"
            >
              Ver todos los artículos
            </button>
          </div>
        )}

        {/* Newsletter / subscribe teaser */}
        {filteredPosts.length > 0 && (
          <div
            className="mt-14 rounded-2xl p-8 text-center border"
            style={{
              background: isLight
                ? 'linear-gradient(135deg, #f0fdff, #f0fff4)'
                : 'linear-gradient(135deg, rgba(0,207,255,0.06), rgba(16,185,129,0.06))',
              borderColor: isLight ? 'rgba(0,207,255,0.2)' : 'rgba(0,207,255,0.12)',
            }}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-4" style={{ backgroundColor: '#00cfff18' }}>
              <i className="ri-mail-line text-electric text-xl" />
            </div>
            <h3 className={`font-heading text-xl font-bold mb-2 ${isLight ? 'text-navy-950' : 'text-white'}`}>
              Mantente informado sobre el e-CF
            </h3>
            <p className={`font-body text-sm mb-6 max-w-md mx-auto ${isLight ? 'text-navy-600' : 'text-white/50'}`}>
              Nuevas guías, cambios normativos y recursos prácticos sobre facturación electrónica en República Dominicana.
            </p>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-electric to-emerald-400 text-navy-950 text-sm font-extrabold px-6 py-2.5 rounded-full cursor-pointer whitespace-nowrap font-body ring-2 ring-electric/30 hover:ring-electric/60 transition-all"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-send-plane-line text-sm" />
              </div>
              Contáctanos
            </Link>
          </div>
        )}
      </main>

      <BlogCTA />
      <Footer />
    </div>
  );
}
